import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import AuthenticateApi from "../../../service/auth.api";
import { useNavigate } from 'react-router-dom';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import { setStorage } from "../../../utils/functions/localstorage";

export default function TermsConsentDialog({ open, onClose, termConsent, token }) {
  const [checked, setChecked] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [scrollToBottom, setScrollToBottom] = useState(false);
  const navigate = useNavigate();

  const policyRef = useRef(null);
  const consentRef = useRef(null);

  const policyHeader = termConsent?.policy?.header;
  const policyDetail = termConsent?.policy?.detail;
  const consentHeader = termConsent?.consent?.header;
  const consentDetail = termConsent?.consent?.detail;

  const steps = [
    // policyHeader || "นโยบายการคุ้มครองข้อมูลส่วนบุคคล",
    "นโยบายการคุ้มครองข้อมูลส่วนบุคคล" || policyHeader,
    consentHeader || "การให้ความยินยอม (Consent)",
  ];

  useEffect(() => {
    if (open) {
      setChecked(false);
      setScrollToBottom(false);
      setActiveStep(0);
    }
  }, [open]);

  const smoothScrollTo = (ref, targetY, duration = 400) => {
    if (!ref) return;
    const startY = ref.scrollTop;
    const distance = targetY - startY;
    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      ref.scrollTop = startY + distance * progress;
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  useEffect(() => {
    setChecked(false);
    setScrollToBottom(false);

    const targetRef = activeStep === 0 ? policyRef.current : consentRef.current;
    if (targetRef) {
        targetRef.scrollTo({ top: 0 });
    }
  }, [activeStep]);

  const handleNext = async () => {
    if (activeStep === 0 && checked) {
      setChecked(false);
      setActiveStep(1);
    }

    if (activeStep === 1 && checked) {
      try {
        const payload = { isAdminApproveConsent: true };
        const res = await AuthenticateApi.postApproveConsentApi(payload, token);
        if (res.success === true) {
          await setStorage('token', token);
          navigate('/');
          window.location.reload();
        }
      } catch (error) {
        console.error("Error sending consent:", error);
      }
    }
  };

  const handleScroll = (e) => {
    const target = e.target;
    const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 5;
    setScrollToBottom(isBottom);
  };

  const handleScrollToBottom = () => {
    const targetRef = activeStep === 0 ? policyRef.current : consentRef.current;
    if (targetRef) {
      smoothScrollTo(targetRef, targetRef.scrollHeight);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setChecked(false);
      setActiveStep(activeStep - 1);
    }
  };

  const ScrollButton = (
    <Button
      onClick={handleScrollToBottom}
      startIcon={<ArrowCircleDownIcon />}
      variant="contained"
      className="z-50"
      sx={{
        maxWidth: '150px',
        minWidth: '150px',
        borderRadius: 5,
        color: '#fff',
        backgroundColor: 'rgba(34, 92, 179, 1)',
        '&:hover': { backgroundColor: 'rgba(34, 63, 179, 1)' },
      }}
    >
      เลื่อนลงล่างสุด
    </Button>
  );

  const renderContent = () => {
    const sharedBoxStyle = {
      fontSize: 14,
      lineHeight: 1.7,
      maxHeight: 300,
      overflowY: 'scroll',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
    };

    if (activeStep === 0) {
      return (
        <>
          <Box sx={{ backgroundColor: '#f5f5f5', padding: 3, borderRadius: 4 }}>
            {policyDetail ? (
              <div className="relative">
                <div
                  ref={policyRef}
                  dangerouslySetInnerHTML={{ __html: policyDetail }}
                  style={sharedBoxStyle}
                  onScroll={handleScroll}
                />
                {!scrollToBottom && (
                    <div className="absolute translate-y-[-120%] translate-x-[-50%] left-[50%]">
                        {ScrollButton}
                    </div>
                )}
              </div>
            ) : (
              <Typography variant="body2" color="text.secondary">
                กำลังโหลดข้อมูลนโยบาย...
              </Typography>
            )}
          </Box>

          <FormControlLabel
            sx={{ mt: 2, '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={!scrollToBottom}
              />
            }
            label="ฉันได้อ่านและยอมรับนโยบายความเป็นส่วนตัว"
          />
        </>
      );
    }

    if (activeStep === 1) {
      return (
        <>
          <Box sx={{ backgroundColor: '#f5f5f5', padding: 3, borderRadius: 4 }}>
            {consentDetail ? (
              <div className="relative">
                <div
                  ref={consentRef}
                  dangerouslySetInnerHTML={{ __html: consentDetail }}
                  style={sharedBoxStyle}
                  onScroll={handleScroll}
                />
                {!scrollToBottom && (
                    <div className="absolute translate-y-[-120%] translate-x-[-50%] left-[50%]">
                        {ScrollButton}
                    </div>
                )}
              </div>
            ) : (
              <Typography variant="body2" color="text.secondary">
                กำลังโหลดข้อมูลการให้ความยินยอม...
              </Typography>
            )}
          </Box>

          <FormControlLabel
            sx={{ mt: 2, '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={!scrollToBottom}
              />
            }
            label="ฉันยินยอมให้ระบบจัดเก็บและใช้ข้อมูลตามที่ระบุ"
          />
        </>
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" scroll="paper">
      <DialogTitle>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{ 
                  "& .MuiStepIcon-text": {
                    fill: "#fff",
                  },
                  "& .MuiStepLabel-label": {
                    fontSize: "0.73rem",
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>
      <DialogContent dividers>{renderContent()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          ปฏิเสธ
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} color="info">
            ย้อนกลับ
          </Button>
        )}
        <Button
          onClick={handleNext}
          variant="contained"
          sx={{ color: '#fff', borderRadius: 3 }}
          color="primary"
          disabled={!checked}
        >
          {activeStep === steps.length - 1 ? "ยืนยัน" : "ถัดไป"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}